/**
 * rehypeCitationMarkers —— 把回答正文里的 `[#N]` 引用标记标出来，供呈现层接上
 * 「定位原文」。
 *
 * 此前正文里的 `[#3]` 只是一串字符：律师看到标记之后，要展开下方引用区、在
 * 几十条里用眼睛找编号为 3 的那条。标记本身可点击之后，正文与原文之间就是一
 * 次点击的距离。
 *
 * 实现放在 rehype 阶段（Markdown 已经解析成 HTML 树）而不是 remark 阶段：
 * remark 阶段可用的链接节点会被 react-markdown 的 URL 安全策略按未知协议清空，
 * 而这里只需要一个带标记编号的 `span`，不涉及任何 URL。
 *
 * 代码块（`code` / `pre`）内的文本不处理——那里的方括号是代码内容，不是引用。
 */

/** 标记编号写在这个属性上，呈现层据此取值。 */
export const CITATION_MARKER_ATTR = "data-citation-index";

/** 本模块用到的最小 hast 结构（避免为一个插件引入额外的类型依赖）。 */
interface HastText {
  type: "text";
  value: string;
}

interface HastElement {
  type: "element";
  tagName: string;
  properties?: Record<string, unknown>;
  children?: HastNode[];
}

interface HastParent {
  type: string;
  tagName?: string;
  children?: HastNode[];
}

type HastNode = HastText | HastElement | HastParent;

/**
 * 单个引用标记。与 utils/citationMarkers 的解析口径一致：接受 `[#3]`，也接受
 * 模型偶发的合并写法 `[#1,#2]`；合并写法整体作为一个标记处理，取第一个编号
 * 作为跳转目标（合并标记本就是一句话对多条依据，跳到第一条是可预期的行为）。
 */
const MARKER_RE = /\[\s*#\s*(\d+(?:\s*[,，、]\s*#?\s*\d+)*)\s*\]/g;

function splitTextNode(node: HastText): HastNode[] | null {
  const value = node.value;
  if (!value.includes("[")) return null;
  const parts: HastNode[] = [];
  let last = 0;
  let matched = false;
  for (const match of value.matchAll(MARKER_RE)) {
    const start = match.index ?? 0;
    const first = Number.parseInt(match[1].replace(/[#\s]/g, "").split(/[,，、]/)[0], 10);
    if (!Number.isInteger(first)) continue;
    matched = true;
    if (start > last) parts.push({ type: "text", value: value.slice(last, start) });
    parts.push({
      type: "element",
      tagName: "span",
      properties: { [CITATION_MARKER_ATTR]: String(first) },
      children: [{ type: "text", value: match[0] }],
    });
    last = start + match[0].length;
  }
  if (!matched) return null;
  if (last < value.length) parts.push({ type: "text", value: value.slice(last) });
  return parts;
}

function walk(node: HastParent): void {
  const children = node.children;
  if (!children || children.length === 0) return;
  const next: HastNode[] = [];
  for (const child of children) {
    if (child.type === "text") {
      const split = splitTextNode(child as HastText);
      if (split) {
        next.push(...split);
        continue;
      }
      next.push(child);
      continue;
    }
    const tagName = (child as HastElement).tagName;
    if (tagName !== "code" && tagName !== "pre") walk(child as HastParent);
    next.push(child);
  }
  node.children = next;
}

/** react-markdown `rehypePlugins` 条目。 */
export function rehypeCitationMarkers() {
  return (tree: HastParent): void => {
    walk(tree);
  };
}
